import { addZero } from "../tools/Tools";

/** A point of the timeline chart: the axis label, the posts counted, and when its period starts (ms). */
export interface TimelinePoint {
    x: string;
    /** Null on the closing point, which only marks where the window ends. */
    y: number | null;
    time: number;
}

/** A day as `/v1/time-line/` counts it: `day` is the date as yyyyMMdd, in the server's zone (UTC). */
export interface DayBucket {
    day: number;
    total: number;
}

const DAY_MS = 24 * 3600 * 1000;

/** The window of a `days`-day view, in epoch seconds: the last `days` UTC days, today's included. */
export function dayWindow(days: number, now: number = Date.now()): { from: number; to: number } {
    const todayStart = now - (now % DAY_MS);
    return {
        from: (todayStart - (days - 1) * DAY_MS) / 1000,
        to: (todayStart + DAY_MS) / 1000 - 1,
    };
}

/**
 * The 7- and 30-day charts: one point per UTC day of the window, oldest first, then a closing point.
 *
 * The chart's x axis is a point scale, which spaces whatever points it gets evenly, and the API sends
 * a bucket only for a day that has posts. So every day of the window is laid out here, at 0 where the
 * API sent nothing; otherwise a day without posts vanishes from the axis and squeezes the others
 * (ops issues/014). Buckets outside the window are ignored.
 *
 * The closing point marks the end of today, so that the range slider, which maps its position onto
 * the time between the first and the last point, reaches today's posts. Its y is null, which nivo
 * leaves out of the line: it used to be 0, and the line dropped to it at the right-hand edge.
 */
export function dayPoints(buckets: DayBucket[], days: number, now: number = Date.now()) {
    const counts = new Map<number, number>();
    buckets.forEach(b => counts.set(b.day, (counts.get(b.day) || 0) + b.total));

    const { from, to } = dayWindow(days, now);
    const timeline: TimelinePoint[] = [];
    let total = 0;
    for (let i = 0; i <= days; i++) {
        const time = from * 1000 + i * DAY_MS;
        const date = new Date(time);
        const x = addZero(date.getUTCDate()) + "/" + addZero(date.getUTCMonth() + 1);
        if (i === days) {
            timeline.push({ x, y: null, time });
            break;
        }
        const key = date.getUTCFullYear() * 10000 + (date.getUTCMonth() + 1) * 100 + date.getUTCDate();
        const y = counts.get(key) || 0;
        total += y;
        timeline.push({ x, y, time });
    }
    return { timeline, from, to, total };
}
